import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {Observable, catchError, map, of, shareReplay, tap, throwError} from 'rxjs';
import {environment} from 'src/environments/environment';
import {SvgBuildInput} from 'src/app/common/models/ai-reports/shared/svg';
import {Asset} from 'src/app/common/models/ai-reports/shared/assets';

/** How long a resolved meta is served from cache before re-fetching. Bounds staleness for mutable assets (pending videos, updated visjs graphs). */
const META_TTL_MS = 30_000;
/** Back-off before re-requesting a meta that has never resolved (true 404s, network down). */
const META_FAILURE_RETRY_MS = 30_000;

/**
 * A backend pod mid-rollout of FT-955 can still respond without `isLikeness` on
 * base64/url assets. Responses aren't Zod-parsed here, so `.default(false)` never
 * fires — normalize explicitly so the EU AI Act Art. 50(4) disclosure check (FT-953)
 * never reads `undefined` where it expects a boolean.
 */
function normalizeLikeness(asset: Asset): Asset {
	if (asset.type === 'base64' || asset.type === 'url') {
		return { ...asset, isLikeness: asset.isLikeness ?? false };
	}
	return asset;
}

@Injectable({ providedIn: 'root' })
export class ClientAssetService {
	private readonly httpClient = inject(HttpClient);
	private readonly sanitizer = inject(DomSanitizer);

	private readonly metaInFlight = new Map<string, Observable<Asset>>();
	private readonly metaLastGood = new Map<string, { asset: Asset; fetchedAt: number }>();
	private readonly metaFailedAt = new Map<string, number>();

	downloadAssetJson<T>(storagePath: string): Observable<T> {
		return this.httpClient.get<T>(
			`${environment.apiBaseUrl}${environment.endpoint.assets.base}/storage`,
			{ params: { path: storagePath } }
		);
	}

	/** Build SVG from asset defs on the server. */
	buildSvg(input: SvgBuildInput) {
		return this.httpClient.post<string>(
			`${environment.apiBaseUrl}${environment.endpoint.assets.buildSvg}`,
			input,
			// Angular HttpClient workaround: responseType 'text' as 'json' allows us to return string
			// while keeping TypeScript happy with the generic return type
			{ responseType: 'text' as 'json' }
		);
	}

	listAll(threadId: string): Observable<Asset[]> {
		return this.httpClient
			.get<Asset[]>(`${environment.apiBaseUrl}/assets/${threadId}`)
			.pipe(map((assets) => assets.map(normalizeLikeness)));
	}

	/**
	 * Asset metadata with a short-TTL cache (FT-901).
	 *
	 * Templates resolve metas on every re-render (assetMeta pipe, report display),
	 * so without a cache each stream event re-fires one GET per asset — and a
	 * transient network failure unrenders every card. Behaviour:
	 * - fresh (< 30s) metas are served from cache; concurrent lookups share one request
	 * - when a re-fetch fails, the last known good meta is served instead of erroring,
	 *   so already-rendered cards survive network blips
	 * - ids that have never resolved are backed off for 30s, then retried, so the
	 *   view recovers once the network returns
	 */
	getMeta(threadId: string, assetId: string): Observable<Asset> {
		const key = `${threadId}:${assetId}`;

		const lastGood = this.metaLastGood.get(key);
		if (lastGood && Date.now() - lastGood.fetchedAt < META_TTL_MS) {
			return of(lastGood.asset);
		}

		const inFlight = this.metaInFlight.get(key);
		if (inFlight) return inFlight;

		const failedAt = this.metaFailedAt.get(key);
		if (failedAt && Date.now() - failedAt < META_FAILURE_RETRY_MS) {
			return lastGood
				? of(lastGood.asset)
				: throwError(() => new Error(`Meta lookup for ${assetId} recently failed`));
		}

		const request$ = this.httpClient
			.get<Asset>(`${environment.apiBaseUrl}/assets/${threadId}/${assetId}/meta`)
			.pipe(
				map(normalizeLikeness),
				tap((asset) => {
					this.metaLastGood.set(key, { asset, fetchedAt: Date.now() });
					this.metaFailedAt.delete(key);
					this.metaInFlight.delete(key);
				}),
				catchError((err) => {
					this.metaFailedAt.set(key, Date.now());
					this.metaInFlight.delete(key);
					// Serve the stale value on refresh failure — never blank an already-rendered card
					if (lastGood) return of(lastGood.asset);
					return throwError(() => err);
				}),
				shareReplay(1)
			);
		this.metaInFlight.set(key, request$);
		return request$;
	}

	/**
	 * Replace stored content for an asset.
	 * Currently supported asset types: visjs only.
	 * Reserved for future visjs graph save UI — no callsite in this migration.
	 */
	updateContent(threadId: string, assetId: string, body: unknown, mimeType: string): Observable<Asset> {
		return this.httpClient.put<Asset>(
			`${environment.apiBaseUrl}/assets/${threadId}/${assetId}/content`,
			body,
			{ headers: { 'Content-Type': mimeType } }
		);
	}

	/**
	 * Convert SVG string to a data: URL safe for use in `<img src>` bindings.
	 * Note: Uses btoa() which is safe only for ASCII/Latin-1 content.
	 * For multi-byte characters, a TextEncoder-based base64 workaround would be needed.
	 */
	getSVGImageUrl(image: string): SafeResourceUrl {
		const base64string = btoa(image);
		// SVG is server-generated (trusted); bypassSecurityTrustResourceUrl is safe here
		// because we control the source and are not rendering user input
		return this.sanitizer.bypassSecurityTrustResourceUrl(
			`data:image/svg+xml;base64,${base64string}`
		);
	}

	postPreview(threadId: string, png: Blob): Observable<void> {
		return this.httpClient.post<void>(
			`${environment.apiBaseUrl}${environment.endpoint.preview}/${threadId}`,
			png
		);
	}

	/** Fetch an image via HttpClient (sends cookies) and return a local blob URL for CSS use. */
	downloadImageAsObjectUrl(url: string): Observable<string> {
		return this.httpClient
			.get(url, { responseType: 'blob', withCredentials: true })
			.pipe(map((blob) => URL.createObjectURL(blob)));
	}

	/** Submit an asset to FTCore for PDF export. */
	publish(
		threadId: string,
		assetId: string,
		body: { template: 1 | 2 | 3; reportName: string }
	): Observable<{ reportId?: string; status?: unknown }> {
		return this.httpClient.post<{ reportId?: string; status?: unknown }>(
			`${environment.apiBaseUrl}${environment.endpoint.assets.base}/${encodeURIComponent(threadId)}/${encodeURIComponent(assetId)}/publish`,
			body
		);
	}

	/** Download an asset from Supabase storage (by storagePath) and return a blob URL. */
	downloadStorageAsObjectUrl(storagePath: string): Observable<string> {
		return this.httpClient
			.get(`${environment.apiBaseUrl}${environment.endpoint.assets.base}/storage`, {
				params: { path: storagePath },
				responseType: 'blob',
			})
			.pipe(map((blob) => URL.createObjectURL(blob)));
	}
}
